import React, { useEffect, useId, useMemo, useState } from "react";
import {
  Box,
  BoxProps,
  Button,
  CircularProgress,
  Paper,
  PaperProps,
  SxProps,
  Typography,
  useTheme,
  useThemeProps,
} from "@mui/material";
import { keyframes } from "@mui/system";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import FontAwesomeSvgIcon from "../../../FontAwesomeSvgIcon";

export type SolidCardProps = {
  title: string;
  children: React.ReactNode;
  elevation?: number;
  sx?: SxProps;
  centerContentVertically?: boolean;
  titleAddon?: React.ReactNode;
  loading?: boolean;
  loadingMessage?: React.ReactNode;
  bodyProps?: BoxProps;
  icon?: IconDefinition;
  testId?: string;
  /** milliseconds before showing loading overlay (default 150) */
  loadingDelay?: number;
  /** custom loading overlay component */
  loadingComponent?: React.ReactNode;
  /** optional error message overlay */
  error?: string;
  /** callback when retrying after an error */
  onRetry?: () => void;
  /** Minimum body height (default: 240) */
  minHeight?: number | string;
  /** Card styling variant */
  variant?: "cardLayout" | "framed";
  /** Override the title Typography variant (defaults to "h6" for cardLayout, "subtitle2" for framed for Platform) */
  titleVariant?: string;
  isCircularLoader?: boolean;
} & Omit<PaperProps, "children" | "title">;

function SolidCard(inProps: SolidCardProps) {
  const props = useThemeProps({ props: inProps, name: "MuiSolidCard" });
  const {
    title,
    children,
    centerContentVertically = true,
    titleAddon,
    elevation = 1,
    bodyProps,
    loading = false,
    loadingMessage,
    loadingDelay = 150,
    loadingComponent,
    error,
    onRetry,
    sx,
    icon,
    testId,
    minHeight = 240,
    variant = "cardLayout",
    titleVariant,
    isCircularLoader = false,
    ...paperProps
  } = props;
  const { sx: bodySx, ...restBodyProps } = bodyProps || {};
  const theme = useTheme();
  const titleId = useId();

  // Always call all hooks for both variants to avoid conditional hook calls
  const shimmer = useMemo(
    () => keyframes`
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    `,
    []
  );

  // delayed loading indicator
  const [showLoading, setShowLoading] = useState(false);
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setShowLoading(true), loadingDelay);
      return () => {
        clearTimeout(timer);
        setShowLoading(false);
      };
    } else {
      setShowLoading(false);
    }
  }, [loading, loadingDelay]);

  // Hide content immediately when loading, even if the overlay delay has not elapsed yet.
  const contentVisibility = {
    opacity: loading ? 0 : 1,
    pointerEvents: loading ? "none" : "auto",
  };
  const showOverlay = loading && showLoading;

  const paperStyles = useMemo(
    () => [
      {
        position: "relative",
        width: "100%",
        overflow: "hidden",
        display: centerContentVertically ? "flex" : "block",
        flexDirection: centerContentVertically ? "column" : undefined,
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        paddingTop: 0,
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ],
    [theme, centerContentVertically, sx]
  );

  const bodyStyles = useMemo(
    () => [
      {
        p: theme.spacing(3),
        flex: 1,
        width: "100%",
        minHeight,
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius,
        display: centerContentVertically ? "flex" : "block",
        flexDirection: centerContentVertically ? "column" : undefined,
        flexGrow: 1,
        alignItems: centerContentVertically || loading ? "center" : undefined,
        justifyContent: centerContentVertically || loading ? "center" : undefined,
        position: "relative",
        transition: "background 0.3s",
      },
      ...(Array.isArray(bodySx) ? bodySx : bodySx ? [bodySx] : []),
    ],
    [theme, centerContentVertically, loading, minHeight, bodySx]
  );

  const loadingStyles = useMemo(
    () => ({
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      minHeight: 120,
      zIndex: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      background: `linear-gradient(-45deg, ${theme.palette.background.default}, ${theme.palette.primary.light}80, ${theme.palette.background.default})`,
      backgroundSize: "400% 400%",
      animation: `${shimmer} 9s ${theme.transitions.easing.easeInOut} infinite`,
      borderRadius: theme.shape.borderRadius,
      transition: "background 0.3s",
    }),
    [theme, shimmer]
  );

  const errorStyles = useMemo(() => {
    if (isCircularLoader) {
      return {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      };
    }
    return {
      ...loadingStyles,
      background: theme.palette.error.main + "33",
    };
  }, [loadingStyles, theme, isCircularLoader]);

  const CircularLoader = () => (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <CircularProgress size={32} />
    </Box>
  );

  // Return CardLayout variant (default) after all hooks are called
  if (variant === "cardLayout") {
    return (
      <Paper
        elevation={elevation}
        data-testid={testId}
        role="region"
        aria-labelledby={titleId}
        sx={[
          {
            width: "100%",
            overflow: "hidden",
            display: centerContentVertically ? "flex" : "block",
            flexDirection: centerContentVertically ? "column" : undefined,
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        {...paperProps}
      >
        {/* Title Section - CardLayout style */}
        <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            {icon && <FontAwesomeSvgIcon icon={icon} fontSize="small" aria-hidden="true" />}
            <Typography
              id={titleId}
              variant={titleVariant ?? "titleMedium"}
              component="div"
              sx={{ textTransform: "capitalize" }}
            >
              {title}
            </Typography>
          </Box>
          <Box>{titleAddon}</Box>
        </Box>
        {/* Body Section - CardLayout style */}
        <Box
          sx={[
            {
              p: 2,
              pt: 0,
              flexGrow: 1,
              position: "relative",
            },
            ...(Array.isArray(bodySx) ? bodySx : bodySx ? [bodySx] : []),
          ]}
          {...restBodyProps}
          aria-busy={loading}
        >
          {showOverlay && (
            <Box
              data-testid="solid-card-loading"
              sx={!isCircularLoader ? loadingStyles : {}}
              role="alert"
              aria-live="polite"
            >
              {isCircularLoader ? CircularLoader() : (loadingComponent ?? loadingMessage ?? null)}
            </Box>
          )}

          {error && (
            <Box data-testid="solid-card-error" sx={errorStyles} role="alert" aria-live="assertive">
              <Typography color="error.main">{error}</Typography>
              {onRetry && (
                <Button size="small" onClick={onRetry} sx={{ mt: theme.spacing(1) }}>
                  Retry
                </Button>
              )}
            </Box>
          )}

          <Box
            component="div"
            sx={{
              ...contentVisibility,
              transition: theme.transitions.create("opacity", {
                duration: theme.transitions.duration.standard,
              }),
            }}
          >
            {children}
          </Box>
        </Box>
      </Paper>
    );
  }

  // Framed variant - original SolidCard logic with frame styling
  return (
    <Paper
      elevation={elevation}
      sx={paperStyles}
      {...paperProps}
      data-testid={testId}
      role="region"
      aria-labelledby={titleId}
    >
      {/* Header */}
      <SolidCardHeader
        id={titleId}
        icon={icon}
        title={title}
        titleAddon={titleAddon}
        titleVariant={titleVariant}
      />

      <Box sx={bodyStyles} {...restBodyProps} role="region" aria-busy={loading}>
        {showOverlay && (
          <Box data-testid="solid-card-loading" sx={loadingStyles} role="alert" aria-live="polite">
            {loadingComponent ?? loadingMessage ?? null}
          </Box>
        )}

        {error && (
          <Box data-testid="solid-card-error" sx={errorStyles} role="alert" aria-live="assertive">
            <Typography color="error.main">{error}</Typography>
            {onRetry && (
              <Button size="small" onClick={onRetry} sx={{ mt: theme.spacing(1) }}>
                Retry
              </Button>
            )}
          </Box>
        )}

        <Box
          component="div"
          sx={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            pointerEvents: contentVisibility.pointerEvents,
            opacity: contentVisibility.opacity,
            transition: theme.transitions.create("opacity", {
              duration: theme.transitions.duration.standard,
            }),
            position: "relative",
          }}
        >
          {children}
        </Box>
      </Box>
    </Paper>
  );
}

// Header sub-component for reuse
const SolidCardHeader: React.FC<{
  id: string;
  title: string;
  icon?: IconDefinition;
  titleAddon?: React.ReactNode;
  titleVariant?: string;
}> = ({ id, title, icon, titleAddon, titleVariant }) => (
  <Box
    component="header"
    pl={1}
    py={2}
    display="flex"
    alignItems="center"
    justifyContent="space-between"
  >
    <Box display="flex" alignItems="center" gap={(theme) => theme.spacing(1)}>
      {icon && <FontAwesomeSvgIcon icon={icon} fontSize="small" aria-hidden="true" />}
      <Typography
        id={id}
        component="h2"
        variant={titleVariant ?? "titleMedium"}
        color="text.primary"
        textTransform="capitalize"
      >
        {title}
      </Typography>
    </Box>
    {titleAddon && (
      <Box ml={(theme) => theme.spacing(2)} display="flex" flex={1} alignItems="center">
        {titleAddon}
      </Box>
    )}
  </Box>
);

export default SolidCard;
